// ── EVIDENCE REPOSITORY ONBOARDING — the client hooks ─────────────────────────────────────────
//
// ⚠ EVERY MUTATION RETURNS THE WHOLE STATUS, AND THE CACHE IS REPLACED FROM IT. The flow's position
// lives on the server, so the client must never compute "what happens next" locally — a page left
// open while the flow moved on elsewhere has to learn that from the server, not from its own guess.
// Setting the query data directly from each response is what makes a refresh and an action agree.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { ConsentCheckResponse, OnboardingStatus } from '@/api/types/evidence-onboarding.types';

const KEY = ['evidence-onboarding', 'status'] as const;

export function useEvidenceOnboarding() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () => {
      const { data } = await apiClient.get('/compliance/evidence/onboarding');
      return data.data as OnboardingStatus;
    },
  });
}

/** ⓘ One hook per real-world act, not per screen — the same discipline the state machine uses. */
function useOnboardingAction<TVars>(path: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: TVars) => {
      const { data } = await apiClient.post(`/compliance/evidence/onboarding${path}`, vars);
      return data.data as OnboardingStatus;
    },
    // ⚠ The server's answer IS the new state. Invalidating and refetching would briefly render the
    //   old gate, which on a two-gate flow reads as the action having failed.
    onSuccess: (status) => qc.setQueryData(KEY, status),
  });
}

export const useStartOnboarding = () => useOnboardingAction<{ repo_kind: string }>('');

export const useConfirmEnable = () => useOnboardingAction<{ directory_id: string }>('/enable');

/**
 * Gate 2 — and the RETRY, deliberately the same call.
 *
 * ⚠ On a retry the admin sends NO `container_ref`: they fixed the grant in their console, not the
 * address, and the server re-probes the ref already on the binding (amendment C).
 */
export const useSubmitScope = () => useOnboardingAction<{
  container_ref?: string; site_id?: string; label?: string;
}>('/scope');

/**
 * §B.1 — repair a BROKEN connection. ⚠ Not a general edit: the server refuses it unless the
 * connection is genuinely broken, and validates before it writes anything.
 */
export const useRepairConnection = () => useOnboardingAction<{
  // ⓘ Box GA S2 (G9) — optional: a Box repair re-checks the stored folder and sends nothing.
  container_ref?: string; site_id?: string;
}>('/repair');

/**
 * Run the connection check on demand.
 *
 * ⚠ It runs the SAME probe the scheduled sweep runs — this only chooses WHEN. The verdict, and
 * whether Repair then appears, come from the existing shared path.
 */
export const useCheckConnectionHealth = () => useOnboardingAction<void>('/health-check');

/**
 * Supply or replace the tenant's repository credential — THE CANONICAL WRITE-ONLY DOOR.
 *
 * ⚠ IT POSTS TO ITS OWN ENDPOINT, NOT THROUGH THE ONBOARDING FLOW. One write path to a secret is
 * the whole of `D-NO-PASTED-CREDENTIALS-SCOPED-TO-CANONICAL-DOOR`; routing it through
 * `/onboarding/*` for UI convenience would create a second one.
 *
 * ⚠ THE RESPONSE CARRIES NO SECRET, AND THE SUBMITTED ONE IS NOT LEFT LYING AROUND. What lands in
 * the query cache is the flow's status projection — `has_credential` and identifiers, never a
 * value. React Query does, however, retain the submitted `variables` on the mutation object until
 * it is reset, so the CALLER must call `reset()` after a successful save. That is asserted in the
 * page's tests rather than assumed here: a cleanup that is only described is a cleanup that has
 * not happened.
 */
export function useSupplyCredential(repoKind: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      client_id: string; enterprise_id: string; client_secret: string;
    }) => {
      await apiClient.post(`/compliance/evidence/credentials/${repoKind}`, vars);
      // ⚠ RE-READ THE FLOW'S OWN STATUS rather than trusting the door's narrower answer. The gate
      //   the admin sees next is computed from the binding, which this endpoint does not return.
      const { data } = await apiClient.get('/compliance/evidence/onboarding');
      return data.data as OnboardingStatus;
    },
    onSuccess: (status) => qc.setQueryData(KEY, status),
  });
}

/**
 * §A — discard a pre-Connected draft. ⚠ Drafts only: the server refuses it for a CONNECTED
 * repository, and this hook is never offered for one. Deleting a connected integration is a
 * different act with its own hook — `useDeleteIntegration` below.
 */
export function useDiscardSetup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.delete('/compliance/evidence/onboarding');
      return data.data as OnboardingStatus;
    },
    onSuccess: (status) => qc.setQueryData(KEY, status),
  });
}

/**
 * Delete the CONNECTED integration (D-CL5-ALLOW-REPO-DISCONNECT).
 *
 * ⚠ The server retires the binding and soft-deletes its integration; evidence, control history and
 * audit records are kept, and nothing outside VeriGRC is touched. It requires the typed confirmation
 * DELETE — the server re-checks it, so the page's gate is not the only one.
 *
 * ⓘ The response is the new status — no binding — so the page returns to "Add a repository".
 */
export const useDeleteIntegration = () => useOnboardingAction<{ confirmation: 'DELETE' }>(
  '/delete-integration');

/**
 * P1.2c — ask the server whether the directory has consented (D-EV-CONSENT-VERIFY).
 *
 * ⚠ NOT A `useOnboardingAction`: the check changes no state, so its answer must NOT replace the
 *   cached status — that would blank the page (the cache holds OnboardingStatus, not this shape).
 */
export function useConsentCheck() {
  return useMutation({
    mutationFn: async (vars: { directory_id: string }) => {
      const { data } = await apiClient.post('/compliance/evidence/onboarding/consent-check', vars);
      return data.data as ConsentCheckResponse;
    },
  });
}
